/**
 * Sprint 56 — Impresión de tickets desde el navegador.
 *
 * En web el servidor no puede tocar la impresora: corre en el VPS y la térmica
 * está colgada del USB de la caja. La única vía es que el navegador del cajero
 * imprima contra su propia impresora, así que montamos el HTML del ticket en un
 * iframe oculto y disparamos su `print()`.
 *
 * Seguridad: el HTML trae datos de BD (nombre de negocio, de productos, del
 * cliente). Aunque el builder los escapa, el iframe va con `sandbox` SIN
 * `allow-scripts`: si algún día se cuela un `<script>` por un campo mal
 * saneado, el navegador se niega a ejecutarlo. `allow-same-origin` es necesario
 * para que esta ventana pueda invocar `print()` sobre el iframe, y
 * `allow-modals` para que el diálogo de impresión tenga permiso de abrirse.
 */

/** Margen de gracia antes de desmontar el iframe si no llega `afterprint`. */
const MS_LIMPIEZA_FALLBACK = 60_000;

/** CSS resuelve a 96 px por pulgada, independientemente del DPI real de la impresora. */
const PX_POR_MM = 96 / 25.4;

/**
 * Cola de papel bajo la última línea. Sin ella el corte queda pegado al texto y
 * el operador cree que se le comió un renglón.
 */
const COLA_MM = 4;

/** Guardas de cordura: ni un ticket de un centímetro ni un rollo entero. */
const ALTO_MIN_MM = 40;
const ALTO_MAX_MM = 900;

export interface ResultadoImpresionNavegador {
  ok: boolean;
  /** Motivo legible cuando no se pudo abrir el diálogo de impresión. */
  error?: string;
}

/**
 * Sprint 156 — Por qué no se hizo cargo el programa de la caja.
 *
 * `sin_agente` es el caso normal en un negocio que nunca instaló la impresión
 * automática: no es un fallo y no se le enseña a nadie. Los otros tres sí son
 * pérdidas: la caja se apagó, la sesión se venció, o algo reventó.
 */
export type MotivoSinAgente =
  | 'sin_agente'
  | 'agente_desconectado'
  | 'error'
  | 'sesion_vencida';

export interface ResultadoImpresion extends ResultadoImpresionNavegador {
  /** `true` = salió en papel por la impresora de la caja, sin diálogo. */
  porAgente?: boolean;
  motivo?: MotivoSinAgente;
}

/**
 * Sprint 58 — Ajusta `@page` al alto real del ticket ya maquetado.
 *
 * El HTML del servidor trae un alto de página fijo de reserva; la impresora
 * térmica avanza y corta al terminar la *página*, no al terminar el texto, así
 * que ese sobrante salía como papel en blanco (reporte de campo: comanda de tres
 * renglones en una tira de 28 cm). Aquí se mide el documento ya paginado y se
 * redefine la página para que termine donde termina el ticket.
 *
 * La medición la hace esta ventana, no el documento del ticket: el iframe sigue
 * sin `allow-scripts`. Devuelve el alto aplicado, o null si no se pudo medir
 * (jsdom, layout aún sin resolver) — en ese caso manda el alto del builder.
 */
export function ajustarAltoPagina(doc: Document, anchoMm: number): number | null {
  const px = Math.max(doc.body?.scrollHeight ?? 0, doc.documentElement?.scrollHeight ?? 0);
  if (!Number.isFinite(px) || px <= 0) return null;

  const alto = Math.min(
    ALTO_MAX_MM,
    Math.max(ALTO_MIN_MM, Math.ceil(px / PX_POR_MM) + COLA_MM),
  );

  const style = doc.createElement('style');
  // Va al final del head: misma especificidad que la regla del builder, gana la última.
  style.textContent = `@page{margin:0;size:${anchoMm}mm ${alto}mm}`;
  (doc.head ?? doc.documentElement).appendChild(style);
  return alto;
}

export async function imprimirHtmlEnNavegador(
  html: string,
  opciones: { anchoMm?: 58 | 80 } = {},
): Promise<ResultadoImpresionNavegador> {
  if (typeof document === 'undefined') {
    return { ok: false, error: 'La impresión sólo está disponible en el navegador.' };
  }

  const anchoMm = opciones.anchoMm ?? 80;

  return new Promise<ResultadoImpresionNavegador>((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('sandbox', 'allow-same-origin allow-modals');
    // Fuera de pantalla en vez de display:none: Chrome no pagina bien un iframe
    // sin layout y el ticket sale en blanco. El ancho es el del papel para que
    // lo que se mide sea la misma maqueta que se imprime.
    iframe.style.cssText =
      `position:fixed;right:0;bottom:0;width:${anchoMm}mm;height:1px;border:0;opacity:0;pointer-events:none`;
    iframe.srcdoc = html;

    let terminado = false;
    let timer: number | undefined;

    const limpiar = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const finalizar = (resultado: ResultadoImpresionNavegador) => {
      if (terminado) return;
      terminado = true;
      // El desmontaje se aplaza: Safari cancela el trabajo si el iframe muere
      // apenas vuelve el print().
      window.setTimeout(limpiar, 1000);
      resolve(resultado);
    };

    iframe.onload = () => {
      const ventana = iframe.contentWindow;
      if (!ventana) {
        finalizar({ ok: false, error: 'No se pudo preparar el ticket para imprimir.' });
        return;
      }

      // `afterprint` cierra el ciclo tanto si el usuario imprime como si cancela.
      ventana.addEventListener('afterprint', () => finalizar({ ok: true }), { once: true });
      timer = window.setTimeout(() => finalizar({ ok: true }), MS_LIMPIEZA_FALLBACK);

      try {
        // Antes de abrir el diálogo: la página debe medir lo que mide el ticket,
        // o la térmica corta al final de una hoja de papel imaginaria.
        if (iframe.contentDocument) ajustarAltoPagina(iframe.contentDocument, anchoMm);
      } catch {
        /* sin medición se imprime con el alto de reserva del builder */
      }

      try {
        ventana.focus();
        ventana.print();
      } catch {
        finalizar({
          ok: false,
          error: 'El navegador bloqueó la impresión. Revisa los permisos de la página.',
        });
      }
    };

    iframe.onerror = () => {
      finalizar({ ok: false, error: 'No se pudo cargar el ticket para imprimir.' });
    };

    document.body.appendChild(iframe);
  });
}

/**
 * Pide el HTML al backend y lo manda a la impresora del navegador.
 * Devuelve `ok:false` con mensaje ya traducido para el operador.
 */
export async function imprimirTicketWeb(
  params: {
    tipo: 'caja' | 'cocina' | 'despacho' | 'prueba';
    pedidoId?: number;
    itemIds?: number[];
  },
): Promise<ResultadoImpresionNavegador> {
  const res = await window.api.ticket.html(params);
  if (!res.ok) {
    return { ok: false, error: res.error ?? 'No se pudo generar el ticket.' };
  }
  return imprimirHtmlEnNavegador(res.data.html, { anchoMm: res.data.anchoMm });
}

/**
 * Sprint 58 — Imprime por el mejor camino disponible.
 *
 * Primero se le ofrece el trabajo al agente de la caja: imprime en ESC/POS, sin
 * diálogo, sin márgenes y con el papel del largo exacto. El servidor responde si
 * se hizo cargo; sólo encola cuando hay un agente vivo, así que un `false` no es
 * un fallo sino "todavía no hay agente aquí" — y entonces se imprime por el
 * navegador igual que antes.
 *
 * El orden importa: nunca se hacen las dos cosas, o el cliente se llevaría dos
 * tickets de la misma venta.
 */
export async function imprimirDocumento(
  params: {
    tipo: 'caja' | 'cocina' | 'despacho' | 'prueba';
    pedidoId?: number;
    itemIds?: number[];
  },
): Promise<ResultadoImpresion> {
  let motivo: MotivoSinAgente | undefined;

  try {
    const cola = await window.api.impresion.encolar(params);
    if (cola.ok && cola.data.encolado) return { ok: true, porAgente: true };
    /**
     * Sprint 156 — Por qué NO se encoló, en vez de tirar el dato.
     *
     * Hasta aquí los dos casos eran indistinguibles para quien llamaba: "este
     * negocio no tiene impresora automática" y "se venció tu sesión" terminaban
     * los dos en el mismo silencio, cayendo al cuadro de imprimir del navegador.
     * El segundo es el que hizo que la dueña tuviera que cerrar y volver a entrar
     * al sistema sin saber por qué eso lo arreglaba.
     */
    motivo = cola.ok
      ? cola.data.motivo
      : 'sesionVencida' in cola && cola.sesionVencida === true
        ? 'sesion_vencida'
        : 'error';
  } catch {
    /* servidor viejo o canal no disponible: se sigue por el navegador */
    motivo = 'error';
  }

  // El navegador no sabe imprimir la orden de entrega en ESC/POS todavía, pero
  // este camino cubre los tres tipos igual que hasta ahora.
  const porNavegador = await imprimirTicketWeb(params);
  return { ...porNavegador, porAgente: false, motivo };
}

/**
 * Sprint 56.1 — Dispara la comanda de cocina si el negocio la tiene activada.
 *
 * En web el equivalente a "imprimir la comanda en la impresora de caja" es
 * simplemente imprimirla: el navegador manda a la única impresora que hay. Se
 * llama desde cada punto donde un pedido entra a cocina.
 *
 * Nunca lanza: si la comanda no sale, el pedido ya está tomado y eso no debe
 * abortarse ni reportarse como fallo de la venta.
 */
export async function imprimirComandaWebSiActiva(
  pedidoId: number,
  itemIds?: number[],
): Promise<void> {
  try {
    const pref = await window.api.config.obtener('web_auto_imprimir_comanda');
    const activa = !pref.ok || pref.data !== 'false';
    if (!activa) return;
    await imprimirDocumento({ tipo: 'cocina', pedidoId, itemIds });
  } catch {
    /* la comanda es un extra: nunca tumba el flujo de venta */
  }
}
